import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  Banknote,
  CheckCircle2,
  Clock,
  RefreshCw,
  ShieldAlert,
  Smartphone,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PlayerFraudIndicatorsPanel, useTimezone, formatInTimezone } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import {
  fetchFraudIndicators,
  fetchPlayerFraudIndicatorDetail,
  fetchPlayerFraudIndicators,
  fetchPlayerFraudWithdrawalAnalysis,
  fetchShopCashiers,
  resolvePlayerFraudIndicator,
} from '../services/api';
import type { WrongDeviceAttempt } from '../types';
import { attemptedActionLabel } from '../utils/cashierDevices';

type FraudTab = 'cashier' | 'player';

type Period = 'daily' | 'weekly' | 'monthly';

type FraudData = {
  from: string;
  to: string;
  thresholds: { highRedemptionThreshold: number; discrepancyThreshold: number };
  highRedemptions: Array<{ cashierId: string; totalRedeem: number }>;
  discrepancies: Array<{
    shiftId: string;
    cashierId: string;
    discrepancyAmount: number;
    openedAt: string;
    closedAt: string;
  }>;
  outsideShiftHours: Array<{
    cashierTransactionId: string;
    cashierId: string;
    shiftId: string;
    occurredAt: string;
    shiftOpenedAt: string | null;
    shiftClosedAt: string | null;
    type: string;
    amount: number;
  }>;
  /** Money operations refused because they did not come from the shift's device. */
  wrongDeviceAttempts?: WrongDeviceAttempt[];
};

type FilterState = {
  period: Period;
  from: string;
  to: string;
  highRedemptionThreshold: string;
  discrepancyThreshold: string;
};

const DEFAULT_FILTERS: FilterState = {
  period: 'daily',
  from: '',
  to: '',
  highRedemptionThreshold: '1000',
  discrepancyThreshold: '10',
};

function asNumber(v: string): number | null {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n;
}

function toIsoFromLocal(local: string): string | undefined {
  const trimmed = local.trim();
  if (!trimmed) return undefined;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

const rangeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatRangeLine(from?: string, to?: string): string | null {
  if (!from || !to) return null;
  const f = new Date(from);
  const t = new Date(to);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return null;
  return `${rangeFormatter.format(f)} → ${rangeFormatter.format(t)}`;
}

function formatLastUpdated(at: Date | null): string {
  if (!at) return '—';
  const sec = Math.floor((Date.now() - at.getTime()) / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  return rangeFormatter.format(at);
}

type RiskTone = 'safe' | 'warning' | 'danger';

function riskTone(count: number): RiskTone {
  if (count <= 0) return 'safe';
  if (count >= 5) return 'danger';
  return 'warning';
}

function riskBadgeLabel(tone: RiskTone): string {
  if (tone === 'safe') return 'Normal';
  if (tone === 'warning') return 'Review';
  return 'Alert';
}

const toneStyles: Record<
  RiskTone,
  { card: string; value: string; badge: string; icon: string }
> = {
  safe: {
    card: 'border-emerald-100 bg-emerald-50/40',
    value: 'text-emerald-800',
    badge: 'bg-emerald-100 text-emerald-800',
    icon: 'text-emerald-600',
  },
  warning: {
    card: 'border-amber-100 bg-amber-50/50',
    value: 'text-amber-900',
    badge: 'bg-amber-100 text-amber-900',
    icon: 'text-amber-600',
  },
  danger: {
    card: 'border-red-100 bg-red-50/50',
    value: 'text-red-900',
    badge: 'bg-red-100 text-red-900',
    icon: 'text-red-600',
  },
};

const controlClass =
  'h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200/80';

const labelClass = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500';

function buildParams(filters: FilterState) {
  const p: Record<string, unknown> = { period: filters.period };
  const fromIso = toIsoFromLocal(filters.from);
  const toIso = toIsoFromLocal(filters.to);
  if (fromIso) p.from = fromIso;
  if (toIso) p.to = toIso;
  const hr = asNumber(filters.highRedemptionThreshold);
  const dt = asNumber(filters.discrepancyThreshold);
  if (hr != null) p.highRedemptionThreshold = hr;
  if (dt != null) p.discrepancyThreshold = dt;
  return p as {
    period: Period;
    from?: string;
    to?: string;
    highRedemptionThreshold?: number;
    discrepancyThreshold?: number;
  };
}

type MetricCardProps = {
  title: string;
  helper: string;
  value: number;
  icon: ReactNode;
};

function MetricCard({ title, helper, value, icon }: MetricCardProps) {
  const tone = riskTone(value);
  const styles = toneStyles[tone];
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-3.5 shadow-sm ${styles.card}`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/80 bg-white shadow-sm ${styles.icon}`}
        aria-hidden
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-slate-600">{title}</p>
          <span className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles.badge}`}>
            {riskBadgeLabel(tone)}
          </span>
        </div>
        <p className={`mt-1 text-2xl font-bold tabular-nums leading-none ${styles.value}`}>{value}</p>
        <p className="mt-1.5 text-[11px] leading-snug text-slate-500">{helper}</p>
      </div>
    </div>
  );
}

type PanelEmptyProps = { icon: ReactNode; title: string; description: string };

function PanelEmpty({ icon, title, description }: PanelEmptyProps) {
  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400">
        {icon}
      </div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="mt-1 max-w-[240px] text-xs leading-relaxed text-slate-500">{description}</p>
    </div>
  );
}

type ResultPanelProps = {
  title: string;
  count: number;
  loading: boolean;
  empty: PanelEmptyProps;
  children: ReactNode;
};

function ResultPanel({ title, count, loading, empty, children }: ResultPanelProps) {
  return (
    <section
      aria-label={title}
      className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
    >
      <header className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600">
          {count}
        </span>
      </header>
      <div className="min-h-[200px] flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-500">Loading…</div>
        ) : count === 0 ? (
          <PanelEmpty {...empty} />
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function CashierFraudIndicatorsTab() {
  const { timezone } = useTimezone();
  const [draftFilters, setDraftFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FraudData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [cashierNames, setCashierNames] = useState<Map<string, string>>(() => new Map());

  // Names for the wrong-device list only; the indicators still render (with ids) if this fails.
  useEffect(() => {
    let cancelled = false;
    fetchShopCashiers()
      .then((res) => {
        if (cancelled || !res.success) return;
        setCashierNames(new Map((res.data || []).map((c) => [c.id, c.name])));
      })
      .catch(() => {
        /* ids are shown instead */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (filters: FilterState) => {
    setLoading(true);
    try {
      const res = await fetchFraudIndicators(buildParams(filters));
      if (!res.success) throw new Error(res.message || 'Failed to load fraud indicators');
      setData((res.data || null) as FraudData | null);
      setLastUpdated(new Date());
    } catch (e: unknown) {
      toast.error((e as { message?: string })?.message || 'Failed to load fraud indicators');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(appliedFilters);
  }, [appliedFilters, load]);

  const highRedemptions = data?.highRedemptions ?? [];
  const discrepancies = data?.discrepancies ?? [];
  const outsideShiftHours = data?.outsideShiftHours ?? [];
  const wrongDeviceAttempts = data?.wrongDeviceAttempts ?? [];

  const rangeLine = useMemo(
    () => formatRangeLine(data?.from, data?.to),
    [data?.from, data?.to],
  );

  const patchDraft = (patch: Partial<FilterState>) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  };

  const onApply = () => {
    setAppliedFilters({ ...draftFilters });
  };

  const onReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const onRefresh = () => {
    void load(appliedFilters);
  };

  return (
    <>
        <div className="flex flex-col gap-4 border-b border-slate-200/80 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <p className="max-w-2xl text-sm leading-snug text-slate-600">
              Monitor suspicious Cashier activity, redemption spikes, transactions outside shift hours, and money
              operations tried from the wrong device.
            </p>
            {rangeLine ? (
              <p className="text-xs font-medium text-slate-500">
                <span className="text-slate-400">Selected range:</span> {rangeLine}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-stretch sm:items-end">
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <p className="mt-1.5 text-[11px] text-slate-500">
              Last updated: <span className="font-medium text-slate-600">{formatLastUpdated(lastUpdated)}</span>
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="High redemptions"
            helper="Cashiers above threshold"
            value={highRedemptions.length}
            icon={<Banknote className="h-4 w-4" />}
          />
          <MetricCard
            title="Discrepancy shifts"
            helper="Shifts above discrepancy limit"
            value={discrepancies.length}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <MetricCard
            title="Outside shift hours"
            helper="Transactions outside schedule"
            value={outsideShiftHours.length}
            icon={<Clock className="h-4 w-4" />}
          />
          <MetricCard
            title="Wrong-device attempts"
            helper="Refused: not on the shift's device"
            value={wrongDeviceAttempts.length}
            icon={<Smartphone className="h-4 w-4" />}
          />
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-12 xl:items-end">
            <div className="xl:col-span-2">
              <label className={labelClass}>Period</label>
              <select
                className={controlClass}
                value={draftFilters.period}
                onChange={(e) => patchDraft({ period: e.target.value as Period })}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="xl:col-span-2">
              <label className={labelClass}>High redemption threshold</label>
              <input
                className={controlClass}
                value={draftFilters.highRedemptionThreshold}
                onChange={(e) => patchDraft({ highRedemptionThreshold: e.target.value })}
                inputMode="decimal"
              />
            </div>
            <div className="xl:col-span-2">
              <label className={labelClass}>Discrepancy threshold</label>
              <input
                className={controlClass}
                value={draftFilters.discrepancyThreshold}
                onChange={(e) => patchDraft({ discrepancyThreshold: e.target.value })}
                inputMode="decimal"
              />
            </div>
            <div className="xl:col-span-2">
              <label className={labelClass}>From</label>
              <input
                type="datetime-local"
                className={controlClass}
                value={draftFilters.from}
                onChange={(e) => patchDraft({ from: e.target.value })}
              />
            </div>
            <div className="xl:col-span-2">
              <label className={labelClass}>To</label>
              <input
                type="datetime-local"
                className={controlClass}
                value={draftFilters.to}
                onChange={(e) => patchDraft({ to: e.target.value })}
              />
            </div>
            <div className="flex flex-wrap gap-2 xl:col-span-2 xl:justify-end">
              <button
                type="button"
                onClick={onApply}
                disabled={loading}
                className="inline-flex h-9 flex-1 items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60 sm:flex-none sm:min-w-[88px]"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={onReset}
                disabled={loading}
                className="inline-flex h-9 flex-1 items-center justify-center rounded-lg border border-transparent px-4 text-sm font-medium text-slate-600 transition hover:border-slate-200 hover:bg-slate-50 disabled:opacity-60 sm:flex-none sm:min-w-[72px]"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <ResultPanel
            title="High redemptions"
            count={highRedemptions.length}
            loading={loading && !data}
            empty={{
              icon: <CheckCircle2 className="h-5 w-5" />,
              title: 'No high-redemption Cashiers found',
              description:
                'No Cashier exceeded the configured redemption threshold in this range.',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[280px] text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 font-medium">Cashier</th>
                    <th className="px-3 py-2 text-right font-medium">Total redeem</th>
                  </tr>
                </thead>
                <tbody>
                  {highRedemptions.map((r) => (
                    <tr key={r.cashierId} className="border-b border-slate-50 last:border-0">
                      <td className="px-3 py-2 font-medium text-slate-800">{r.cashierId}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-amber-800">
                        {r.totalRedeem}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ResultPanel>

          <ResultPanel
            title="Discrepancy shifts"
            count={discrepancies.length}
            loading={loading && !data}
            empty={{
              icon: <CheckCircle2 className="h-5 w-5" />,
              title: 'No discrepancy shifts found',
              description: 'All shifts are within the configured discrepancy threshold.',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[320px] text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 font-medium">Shift</th>
                    <th className="px-3 py-2 font-medium">Cashier</th>
                    <th className="px-3 py-2 text-right font-medium">Discrepancy</th>
                  </tr>
                </thead>
                <tbody>
                  {discrepancies.map((s) => (
                    <tr key={s.shiftId} className="border-b border-slate-50 last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{s.shiftId}</div>
                        <div className="mt-0.5 text-[10px] text-slate-500">
                          {formatInTimezone(s.openedAt, timezone)} → {formatInTimezone(s.closedAt, timezone)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{s.cashierId}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">
                        {s.discrepancyAmount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ResultPanel>

          <ResultPanel
            title="Outside shift hours"
            count={outsideShiftHours.length}
            loading={loading && !data}
            empty={{
              icon: <CheckCircle2 className="h-5 w-5" />,
              title: 'No outside-hours transactions found',
              description: 'Transactions matched configured Cashier shift hours.',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[360px] text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 font-medium">Transaction</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {outsideShiftHours.map((t) => (
                    <tr key={t.cashierTransactionId} className="border-b border-slate-50 last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{t.cashierTransactionId}</div>
                        <div className="mt-0.5 text-[10px] text-slate-500">
                          Cashier {t.cashierId} · {formatInTimezone(t.occurredAt, timezone)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <ArrowLeftRight className="h-3 w-3 text-slate-400" aria-hidden />
                          {t.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800">{t.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ResultPanel>
        </div>

        <div className="mt-4">
          <ResultPanel
            title="Wrong-device attempts"
            count={wrongDeviceAttempts.length}
            loading={loading && !data}
            empty={{
              icon: <CheckCircle2 className="h-5 w-5" />,
              title: 'No wrong-device attempts found',
              description: 'Every money operation in this range came from the device its shift runs on.',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 font-medium">Cashier</th>
                    <th className="px-3 py-2 font-medium">Attempted action</th>
                    <th className="px-3 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 text-right font-medium">Shift</th>
                  </tr>
                </thead>
                <tbody>
                  {wrongDeviceAttempts.map((a, index) => (
                    <tr
                      key={`${a.shiftId ?? 'none'}-${a.occurredAt}-${index}`}
                      className="border-b border-slate-50 last:border-0"
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {cashierNames.get(a.cashierId) || a.cashierId}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{attemptedActionLabel(a.attemptedAction)}</td>
                      <td className="px-3 py-2 text-slate-700">{formatInTimezone(a.occurredAt, timezone)}</td>
                      <td className="px-3 py-2 text-right">
                        {a.shiftId ? (
                          <Link
                            to={`/shifts/${encodeURIComponent(a.shiftId)}`}
                            className="font-medium text-emerald-700 hover:underline"
                          >
                            View shift
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ResultPanel>
        </div>

        {(highRedemptions.length > 0 ||
          discrepancies.length > 0 ||
          outsideShiftHours.length > 0 ||
          wrongDeviceAttempts.length > 0) && (
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
            Review flagged items against shift logs and Cashier permissions before escalating.
          </p>
        )}
    </>
  );
}

export default function FraudIndicatorsPage() {
  const [tab, setTab] = useState<FraudTab>('cashier');
  const handlePlayerFraudError = useCallback((msg: string) => {
    toast.error(msg);
  }, []);
  const handlePlayerFraudSuccess = useCallback((msg: string) => {
    toast.success(msg);
  }, []);

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none bg-slate-50/90 px-1 pb-8 sm:px-0">
        <header className="border-b border-slate-200/80 pb-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-500" />
            <h1 className="text-lg font-semibold tracking-tight text-slate-900">Fraud indicators</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Cashier activity and Player transaction fraud flags for your Shop.
          </p>
          <div className="mt-4 flex gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm w-fit">
            <button
              type="button"
              onClick={() => setTab('cashier')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                tab === 'cashier' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Banknote className="h-4 w-4" />
              Cashier indicators
            </button>
            <button
              type="button"
              onClick={() => setTab('player')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                tab === 'player' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Users className="h-4 w-4" />
              Player indicators
            </button>
          </div>
        </header>

        <div className="mt-4">
          {tab === 'cashier' ? (
            <CashierFraudIndicatorsTab />
          ) : (
            <PlayerFraudIndicatorsPanel
              fetchList={fetchPlayerFraudIndicators}
              fetchDetail={fetchPlayerFraudIndicatorDetail}
              fetchWithdrawalAnalysis={fetchPlayerFraudWithdrawalAnalysis}
              resolveIndicator={resolvePlayerFraudIndicator}
              onError={handlePlayerFraudError}
              onSuccess={handlePlayerFraudSuccess}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
